<template>
  <div class="p-3 bg-white rounded shadow-sm" data-test="result-orchestrator" :data-orchestrator="`result_${contest.reference}`">
    <h3>{{ contest.title[$i18n.locale] }}</h3>

    <div class="accent-line mt-2 mb-4"></div>
    <div v-if='resultOrchestrator.error'>
      <details class='alert alert-warning mb-2'>
        <summary class='fw-bold'>{{ $t("js.election_client.error.headers.an_error_occurred") }}</summary>
        <div>
          <p class='mb-0'>{{ $t("backend.e2e.voting_round_reports.show.error_body") }}</p>
        </div>
      </details>
    </div>
    <div v-if="result && result.resultData">
      <div v-if="contest.resultMethod == 'multi_seat_instant_runoff'"  class='vstack gap-3'>
        <AVInstantRunoffSummary
          v-for="(seat, index) in result.resultDataForDisplay"
          :sortedResult='seat.sortedResult'
          :rounds='result.resultData.seatsData[index].rounds'
          :seatNumber='Number(index) + 1'
          :quota='result.resultData.seatsData[index].quota'
          :totalVotes='result.resultData.seatsData[index].totalVotes'
          :blankVotes='result.resultData.seatsData[index].exclusiveOptionCounts.blank || 0'
          :votesNotIncluded='seat.votesNotIncluded'
          :vote-counts="result.voterCounts"
        />
      </div>
      <AVInstantRunoffSummary
        v-else-if="contest.resultMethod == 'instant_runoff'"
        :sortedResult='result.resultDataForDisplay'
        :rounds='result.resultData.rounds'
        :seatNumber='1'
        :quota='result.resultData.quota'
        :totalVotes='result.resultData.totalVotes'
        :blankVotes='result.resultData.exclusiveOptionCounts.blank || 0'
        :votesNotIncluded='null'
        :vote-counts="result.voterCounts"
      />
      <AVRankedSummary
        v-else-if="contest.resultMethod == 'prioritization'"
        :result="result.resultDataForDisplay"
        :distribution-number="result.resultData.distributionNo"
        :hide-tied="!votingRoundReport.colorTied"
        :hide-elected="!votingRoundReport.colorElected"
        :seats="contest.seats ?? 1"
        :vote-counts="result.voterCounts"
      />
      <AVDhondtSummary
        v-else-if="contest.resultMethod == 'dhondt'"
        :result="result.resultDataForDisplay"
        :total-count="totalCount"
        :distribution-number="result.resultData.distributionNo"
        :hide-tied="!votingRoundReport.colorTied"
        :hide-elected="!votingRoundReport.colorElected"
        :seats="contest.seats ?? 1"
        :vote-counts="result.voterCounts"
      />
      <AVDhondtAPSummary
        v-else-if="contest.resultMethod == 'dhondt_ap'"
        :result="result.resultDataForDisplay"
        :total-count="totalCount"
        :distribution-number="result.resultData.distributionNo"
        :hide-tied="!votingRoundReport.colorTied"
        :hide-elected="!votingRoundReport.colorElected"
        :seats="contest.seats ?? 1"
        :vote-counts="result.voterCounts"
      />
      <AVNormalSummary
        v-else
        :sortedResult="result.resultDataForDisplay"
        :total-count="totalCount"
        :hide-percentage="!contest.resultPercentages"
        :hide-tied="!votingRoundReport.colorTied"
        :hide-elected="!votingRoundReport.colorElected"
        :disregard-blank="contest.disregardBlankVotes"
        :vote-counts="result.voterCounts"
      />
      <div class='mt-4 small'>
        <TurnoutTable :turnout='result.voterCounts'  :hide-weight='contest.disregardWeights' />
      </div>

      <div class="mt-4 small">
        {{ $t('backend.e2e.voting_round_reports.show.invalid_votes') }}: {{ result.voterCounts.invalidVotes }} <br />
        {{ $t('backend.e2e.voting_round_reports.show.disabled_votes') }}: {{ result.voterCounts.disabledWeight }}
      </div>

      <details class="mt-3" v-if="externalVotes">
        <summary>{{ $t('backend.e2e.voting_round_reports.show.external_votes') }}</summary>
        <ul class="list-group border-0">
          <li v-for="optionTitle in Object.keys(externalVotes)" class="list-group-item d-flex justify-content-between align-items-center">
            {{ optionTitle }}
            <template v-if="typeof(externalVotes[optionTitle]) == 'object'">
              <div class="w-25">
                <ul class="list-group">
                  <li class="border-0 list-group-item d-flex justify-content-between w-100" v-for="rank in Object.keys(externalVotes[optionTitle])">
                    {{ $t("backend.e2e.voting_round_reports.show.rank_n", { rank: parseInt(rank) + 1 })}}: {{  externalVotes[optionTitle][rank] }}
                  </li>
                </ul>
              </div>

            </template>
            <template v-else>
              <span>{{ externalVotes[optionTitle] }}</span>
            </template>
          </li>
        </ul>
      </details>

      <details class="mt-3">
        <summary>{{ $t('backend.e2e.voting_round_reports.show.ceremony_details') }}</summary>
        <div class="mt-3">
          <table class="table table-borderless">
            <thead>
              <tr>
                <th><i class="fas fa-user me-2"></i> {{ $t('activerecord.models.e2e/trustee.one') }}</th>
                <th><i class="fas fa-shuffle me-2"></i> {{ $t('js.result_progress.states.mixing') }}</th>
                <th><i class="fas fa-key me-2"></i> {{ $t('js.result_progress.states.decrypting') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="trustee in trustees">
                <td>{{ trustee.name }}</td>
                <td>
                  <i class="fas fa-check text-success" v-if="calculateTrusteeMixingStatus(trustee) == 'finished'"></i>
                  <i
                    class="fas fa-times text-danger"
                    v-else-if="calculateTrusteeMixingStatus(trustee) == 'cancelled'"
                  ></i>
                  <i class="fas fa-ellipsis text-muted" v-else></i>
                </td>
                <td>
                  <i
                    class="fas fa-check text-success"
                    v-if="calculateTrusteeDecryptionStatus(trustee) == 'finished'"
                  ></i>
                  <i
                    class="fas fa-times text-danger"
                    v-else-if="calculateTrusteeDecryptionStatus(trustee) == 'cancelled'"
                  ></i>
                  <i class="fas fa-ellipsis text-muted" v-else></i>
                </td>
              </tr>
            </tbody>
          </table>
          <a
            class="btn btn-sm btn-link"
            :href="`../../../e2e/result_orchestrators/${resultOrchestrator.id}/download_audit_zip`"
          >
            <i class="fas fa-gavel"></i> {{ $t('backend.e2e.threshold_ceremony.index.download_audit_zip') }}
          </a>
        </div>
      </details>
    </div>
    <div v-else-if="resultOrchestrator.status === 'mapping'">
      <WriteInMap :result-orchestrator="resultOrchestrator" :contest="contest" />
    </div>
    <div v-else>
      <div class="hstack align-items-center gap-2 mb-2">
        <b>Status:</b>
        <span class="me-2">{{ $t(`js.result_progress.states.${resultOrchestrator.status}`) }}</span>
      </div>
      <ResultOrchestratorProgress
        :id="`result_orchestrator_${resultOrchestrator.id}`"
        :result-orchestrator="resultOrchestrator"
        :trustee-count="trustees.length"
        class="mb-5"
      />

      <table class="table table-borderless">
        <thead>
          <tr>
            <th><i class="fas fa-user me-2"></i> {{ $t('activerecord.models.e2e/trustee.one') }}</th>
            <th><i class="fas fa-shuffle me-2"></i> {{ $t('js.result_progress.states.mixing') }}</th>
            <th><i class="fas fa-key me-2"></i> {{ $t('js.result_progress.states.decrypting') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="trustee in trustees">
            <td>
              <div class="hstack gap-2">
                <i class="fas fa-spin fa-spinner" v-if="trustee.uuid == activeTrustee"></i>
                <div
                  :class="trustee.connected ? 'bg-success pulse-success' : 'bg-danger'"
                  class="circle-color"
                  v-tooltip="
                    trustee.connected
                      ? $t('backend.e2e.voting_round_reports.show.present')
                      : $t('backend.e2e.voting_round_reports.show.not_present')
                  "
                >

              </div>
                {{ trustee.name }}
              </div>
            </td>
            <td>
              <div class="hstack justify-content-between me-4">
                <span>{{ calculateTrusteeMixingStatus(trustee) }}</span>
                <div class="hstack gap-3">
                  <a
                    @click="retryMixing(trustee)"
                    role="button"
                    v-if="['initial', 'starting', 'mixing', 'validating', 'failed'].includes(calculateTrusteeMixingStatus(trustee))"
                    v-tooltip="$t('backend.actions.retry')"
                    class="link-dark"
                  >
                    <i class="fas fa-repeat"></i>
                  </a>
                  <a
                    @click="cancelMixing(trustee)"
                    role="button"
                    v-if="
                      ['initial', 'starting', 'mixing', 'validating', 'failed'].includes(calculateTrusteeMixingStatus(trustee)) &&
                      canCancelMixing
                    "
                    v-tooltip="$t('backend.actions.skip')"
                    class="link-danger"
                  >
                    <i class="fas fa-times"></i>
                  </a>
                </div>
              </div>
            </td>
            <td>
              <div class="hstack justify-content-between">
                <span>{{ calculateTrusteeDecryptionStatus(trustee) }}</span>
                <div class="hstack gap-3" v-if="resultOrchestrator.status == 'decrypting'">
                  <a
                    @click="retryDecryption(trustee)"
                    role="button"
                    v-if="
                      ['initial', 'decrypting', 'validating', 'failed'].includes(
                        calculateTrusteeDecryptionStatus(trustee),
                      )
                    "
                    v-tooltip="$t('backend.actions.retry')"
                    class="link-dark"
                  >
                    <i class="fas fa-repeat"></i>
                  </a>
                  <a
                    @click="cancelDecryption(trustee)"
                    role="button"
                    v-if="
                      ['initial', 'decrypting', 'validating', 'failed'].includes(
                        calculateTrusteeDecryptionStatus(trustee),
                      ) && canCancelDecryption
                    "
                    v-tooltip="$t('backend.actions.skip')"
                    class="link-danger"
                  >
                    <i class="fas fa-times"></i>
                  </a>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ResultOrchestratorProgress from '@/components/backend/ResultOrchestratorProgress.vue';
import IndeterminateSpinner from '@/components/backend/live/IndeterminateSpinner.vue';
import apiClient from '@/entrypoints/frontend/apiClient';
import type { ConferenceContest, ElectaTrustee, PropType, ResultOrchestrator, ConferenceVotingRoundReport, ConferenceOption } from '@/types';
import { flattenOptions } from '@/entrypoints/shared/contest_utilities';
import TurnoutTable from '@/components/backend/live/TurnoutTable.vue';
import WriteInMap from './WriteInMap.vue';

export default defineComponent({
  name: 'ResultOrchestrator',
  components: { TurnoutTable, IndeterminateSpinner, ResultOrchestratorProgress, WriteInMap },
  props: {
    resultOrchestrator: Object as PropType<ResultOrchestrator>,
    votingRoundReport: Object as PropType<ConferenceVotingRoundReport>,
    contest: Object as PropType<ConferenceContest>,
    trustees: Array as PropType<Array<ElectaTrustee>>,
    threshold: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      packageSuccessStatus: ['initial', 'starting', 'mixing', 'decrypting', 'validating', 'finished'],
      inProgressStatus: ['starting', 'mixing', 'decrypting', 'validating'],
    };
  },
  computed: {
    activeTrustee() {
      return this.resultOrchestrator.mixingPackages?.find(mixPackage => this.inProgressStatus.includes(mixPackage.status))?.trusteeUUID
    },
    result() {
      return this.resultOrchestrator.result;
    },
    externalVotes(): undefined | Record<string, number | Record<string, number>> {
      let externalVotes = {}
      const resultData = this.result.resultData.externalVotes?.[this.contest.reference]
      if (!resultData) return;

      Object.values(this.optionsFlattened).forEach ( (option: ConferenceOption) => {
        if (!option.reference || option.reference == "blank") return

        externalVotes[option.title[this.$i18n.locale] || Object.values(option.title)[0]] = resultData[option.reference]
      })

      if (this.contest.blankOption !== "disabled") {
        externalVotes[this.$i18n.t("js.standard_option.blank")] = resultData['blank']
      }

      return externalVotes
    },
    optionsFlattened() {
      return this.flattenOptions(this.contest.options)
    },
    totalCount() {
      if (this.result === null || this.result.resultData === null) {
        return 0;
      }
      if (this.contest.disregardBlankVotes) {
        return Object.entries(this.result.resultData?.optionCounts)
          .filter(([reference, _count]) => !this.result.resultData?.exclusiveOptions.includes(reference.toString()))
          .reduce((sum: number, [_reference, count]: any[]) => sum + count, 0);
      } else if (this.contest.consensusRequired && this.contest.countUnvoicedVotesAsBlank) {
        return this.contest.disregardWeight
          ? this.result.voterCounts.activeEligiblesOrVoted
          : this.result.voterCounts.activeEligiblesOrVotedWeight;
      } else {
        const excludedOptions = this.result.resultData?.exclusiveOptions.filter(reference => reference !== "blank")

        return Object.entries(this.result.resultData?.optionCounts)
          .filter(([reference, _count]) => !excludedOptions.includes(reference))
          .reduce((sum: number, [_reference, count]: any[]) => sum + count, 0);
      }
    },
    canCancelMixing() {
      // As long as there is enough packages that isn't cancelled or failed we are allowed to cancel
      return (
        this.resultOrchestrator.mixingPackages?.filter((mixPackage) =>
          this.packageSuccessStatus.includes(mixPackage.status),
        ).length > this.threshold
      );
    },
    canCancelDecryption() {
      // As long as there is enough packages that isn't cancelled or failed we are allowed to cancel
      return (
        this.resultOrchestrator.decryptionPackages?.filter((decPackage) =>
          this.packageSuccessStatus.includes(decPackage.status),
        ).length > this.threshold
      );
    },
  },
  methods: {
    flattenOptions(a) {
      return a.reduce(function (flattened, {component, children}) {

        return flattened
          .concat([{children, component}])
          .concat(children ? flattenOptions(a) : []);
      }, []);
    },
    trusteeMixingPackage(trustee) {
      return this.resultOrchestrator.mixingPackages?.find((mixPackage) => mixPackage.trusteeUUID === trustee.uuid);
    },
    trusteeDecryptionPackage(trustee) {
      return this.resultOrchestrator.decryptionPackages?.find((dPackage) => dPackage.trusteeUUID === trustee.uuid);
    },
    calculateTrusteeMixingStatus(trustee) {
      return this.trusteeMixingPackage(trustee)?.status || 'initial';
    },
    calculateTrusteeDecryptionStatus(trustee) {
      return this.trusteeDecryptionPackage(trustee)?.status || 'initial';
    },
    async cancelMixing(trustee) {
      const url = `${window.location.href.split('/voting_rounds/')[0]}/e2e/trustees/cancel_mixing`;

      await apiClient.post(url, {
        id: trustee.id,
        mixing_package_id: this.resultOrchestrator.mixingPackages?.find(
          (mixPackage) => mixPackage.trusteeUUID === trustee.uuid,
        ).id,
      });
    },
    async cancelDecryption(trustee) {
      const url = `${window.location.href.split('/voting_rounds/')[0]}/e2e/trustees/cancel_decryption`;
      await apiClient.post(url, {
        decryption_package_id: this.resultOrchestrator.decryptionPackages?.find(
          (decryptPackage) => decryptPackage.trusteeUUID === trustee.uuid,
        ).id,
        id: trustee.id,
      });
    },
    async retryDecryption(trustee) {
      const url = `${window.location.href.split('/voting_rounds/')[0]}/e2e/trustees/retry_decryption`;
      await apiClient.post(url, {
        decryption_package_id: this.resultOrchestrator.decryptionPackages?.find(
          (decryptPackage) => decryptPackage.trusteeUUID === trustee.uuid,
        ).id,
        id: trustee.id,
      });
    },
    async retryMixing(trustee) {
      const url = `${window.location.href.split('/voting_rounds/')[0]}/e2e/trustees/retry_mixing`;
      await apiClient.post(url, {
        id: trustee.id,
        mixing_package_id: this.resultOrchestrator.mixingPackages?.find(
          (mixPackage) => mixPackage.trusteeUUID === trustee.uuid,
        ).id,
      });
    },
  },
});
</script>
<style scoped></style>
